import { useState, useEffect, useMemo } from 'react';
import { getAllWorkOrders, type WorkOrderResponse } from '@/api/workorder.api';
import bomAPI from '@/api/bom.api';
import { formatDateIST } from '@/utils/format';
import { getFullProductionOrderDetails } from '@/api/productionOrder.api';
import { useWorkCenters } from '@/hooks/useWorkCenters';

interface POPrintModalProps {
  po?: any;
  order?: any;
  workOrders?: any[];
  materials?: any[];
  onClose: () => void;
}

const woStatusIcon: Record<string, string> = {
  PENDING: 'ri-time-line',
  IN_PROGRESS: 'ri-loader-4-line',
  COMPLETED: 'ri-check-line',
  ON_HOLD: 'ri-pause-circle-line',
  CANCELLED: 'ri-close-circle-line',
};

export default function POPrintModal({
  po: propPo,
  order,
  workOrders: initialWorkOrders,
  materials: initialMaterials,
  onClose,
}: POPrintModalProps) {
  // Work center shown as "WC-001 · Assembly Line"
  const workCenterLookup = useWorkCenters();
  const po = propPo || order;
  const [fetchedDetails, setFetchedDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const poId = po?.id;

  useEffect(() => {
    // If workOrders and materials are not passed, and we have a valid poId, fetch full details
    if (
      (!initialWorkOrders || initialWorkOrders.length === 0) &&
      (!initialMaterials || initialMaterials.length === 0) &&
      poId &&
      typeof poId === 'string' &&
      poId.length > 10
    ) {
      let isMounted = true;
      setLoading(true);
      getFullProductionOrderDetails(poId)
        .then((res) => {
          if (isMounted && res.success && res.data) {
            setFetchedDetails(res.data);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch PO print details:', err);
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });

      return () => {
        isMounted = false;
      };
    }
  }, [poId, initialWorkOrders, initialMaterials]);

  // Normalize Production Order fields (handling both camelCase & snake_case)
  const poData = useMemo(() => {
    const raw = fetchedDetails?.order || po || {};
    return {
      id: raw.id || '',
      poNumber: raw.poNumber || raw.po_number || '',
      type: raw.type || '',
      status: raw.status || '',
      priority: raw.priority || '',
      itemName: raw.itemName || raw.item_name || raw.productName || '—',
      itemCode: raw.itemCode || raw.item_code || raw.productCode || '—',
      unit: raw.unit || raw.item_unit || '',
      plannedQty: Number(raw.plannedQty ?? raw.planned_qty ?? 1),
      completedQty: Number(raw.completedQty ?? raw.completed_qty ?? 0),
      plannedStartDate: raw.plannedStartDate || raw.planned_start_date || '',
      plannedEndDate: raw.plannedEndDate || raw.planned_end_date || '',
      bomVersion: raw.bomVersion || raw.bom_version || raw.bom_code || 'v1.0',
      salesOrderRef: raw.salesOrderRef || raw.sales_order_ref || raw.sales_invoice_id || null,
      warehouseName: raw.warehouseName || raw.warehouse_name || '—',
      notes: raw.notes || '',
    };
  }, [po, fetchedDetails]);

  // Normalize Work Orders array
  const workOrdersList = useMemo(() => {
    if (initialWorkOrders && initialWorkOrders.length > 0) return initialWorkOrders;
    if (fetchedDetails?.workOrders && fetchedDetails.workOrders.length > 0) return fetchedDetails.workOrders;
    if (po?.workOrders && po.workOrders.length > 0) return po.workOrders;
    if (po?.work_orders && po.work_orders.length > 0) return po.work_orders;

    return [];
  }, [initialWorkOrders, fetchedDetails, po]);

  // Normalize Materials list
  const materialsList = useMemo(() => {
    if (initialMaterials && initialMaterials.length > 0) return initialMaterials;
    if (fetchedDetails?.materials && fetchedDetails.materials.length > 0) return fetchedDetails.materials;
    if (po?.materials && po.materials.length > 0) return po.materials;

    return [];
  }, [initialMaterials, fetchedDetails, po]);

  // Company header comes with the order details
  const company = (order as any)?.company || fetchedDetails?.order?.company || (propPo as any)?.company || null;
  const companyName: string = company?.name || '';
  const companyAddress = [company?.address, company?.address_line2, company?.city, company?.pincode].filter(Boolean).join(', ');
  const companyInitials = companyName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8 print:p-0 print:bg-white print:static print:inset-auto"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl rounded-2xl w-[900px] max-w-[95vw] mx-auto overflow-hidden border border-slate-200 print:shadow-none print:border-none print:w-full print:max-w-none print:rounded-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Printable Area */}
        <div id="po-print-area" className="p-8 print:p-0 text-slate-800">
          
          {/* ── COMPANY & DOCUMENT HEADER ── */}
          <div className="border-b-2 border-slate-900 pb-4 mb-6 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-slate-900 text-white font-bold flex items-center justify-center text-sm print:border print:border-black print:text-black print:bg-white">
                  {companyInitials}
                </span>
                <div>
                  <h1 className="text-xl font-extrabold text-slate-900 tracking-tight uppercase">
                    {companyName}
                  </h1>
                  <p className="text-xs text-slate-500 font-medium">{companyAddress}</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-1 font-mono">
                {[company?.gstin ? `GSTIN: ${company.gstin}` : '', company?.phone ? `Phone: ${company.phone}` : ''].filter(Boolean).join(' · ')}
              </p>
            </div>

            <div className="text-right">
              <span className="inline-block px-3 py-1 bg-slate-900 text-white text-xs font-bold uppercase tracking-wider rounded print:border print:border-black print:bg-white print:text-black">
                Production Order Traveler Sheet
              </span>
              <p className="text-xs font-mono font-bold text-slate-700 mt-1">
                {poData.poNumber}
              </p>
              {/* Visual Simulated Barcode */}
              <div className="mt-1 flex justify-end">
                <div className="font-mono text-lg font-bold tracking-widest bg-slate-100 print:bg-white px-2 py-0.5 border border-slate-300 rounded text-slate-900 select-none">
                  ||| || ||| | |||| | |||
                </div>
              </div>
            </div>
          </div>

          {/* ── KEY ORDER METRICS GRID ── */}
          <div className="grid grid-cols-2 gap-4 mb-6 text-xs bg-slate-50 print:bg-white p-4 rounded-xl border border-slate-200">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">PO Number:</span>
                <span className="font-bold text-slate-900 font-mono">{poData.poNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">Order Type:</span>
                <span className="font-bold text-slate-800 uppercase">{poData.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">Priority:</span>
                <span className="font-semibold text-slate-800">{poData.priority}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">Warehouse / Plant:</span>
                <span className="font-medium text-slate-800">{poData.warehouseName}</span>
              </div>
              {poData.salesOrderRef && (
                <div className="flex justify-between">
                  <span className="text-slate-500 font-medium">Sales Order Ref:</span>
                  <span className="font-bold text-indigo-700 font-mono">{poData.salesOrderRef}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">Planned Start:</span>
                <span className="font-semibold text-slate-800">{formatDateIST(poData.plannedStartDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">Planned Completion:</span>
                <span className="font-semibold text-slate-800">{formatDateIST(poData.plannedEndDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">BOM Version:</span>
                <span className="font-mono text-slate-800 font-semibold">{poData.bomVersion}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">Order Status:</span>
                <span className="font-bold text-slate-800 uppercase">{poData.status}</span>
              </div>
            </div>
          </div>

          {/* ── FINISHED ITEM DETAILS ── */}
          <div className="border border-slate-300 rounded-xl p-4 mb-6 bg-slate-50/50 print:bg-white">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-2 flex items-center justify-between border-b border-slate-200 pb-1">
              <span>Finished Product Specification</span>
              <span className="text-indigo-600 font-mono text-[11px]">QTY: {poData.plannedQty} {poData.unit}</span>
            </h3>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div>
                <p className="text-slate-500 font-medium">Item Description</p>
                <p className="font-bold text-slate-900 mt-0.5 text-sm">{poData.itemName}</p>
              </div>
              <div>
                <p className="text-slate-500 font-medium">Item Code / SKU</p>
                <p className="font-mono font-bold text-slate-800 mt-0.5">{poData.itemCode}</p>
              </div>
              <div>
                <p className="text-slate-500 font-medium">Batch / Order Qty</p>
                <p className="font-bold text-slate-900 font-mono mt-0.5 text-sm">
                  {poData.plannedQty} <span className="text-xs text-slate-600 font-normal">{poData.unit}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Loading Spinner during background detail fetch */}
          {loading && (
            <div className="py-6 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin text-indigo-600 text-base" />
              Loading Routing & Material Details…
            </div>
          )}

          {/* ── WORK ORDERS / ROUTING STAGES CHECKLIST ── */}
          <div className="mb-6">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-2 flex items-center justify-between">
              <span>Manufacturing Routing & Operator Signoff Checklist</span>
              <span className="text-[10px] text-slate-400 font-normal">Stages: {workOrdersList.length}</span>
            </h3>
            
            <table className="w-full text-xs border border-slate-300 border-collapse">
              <thead>
                <tr className="bg-slate-100 print:bg-slate-200 text-slate-800 font-bold border-b border-slate-300">
                  <th className="border border-slate-300 px-2 py-1.5 text-center w-10">Stg #</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Stage & Description</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Work Center / Machine</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-right w-16">Time (m)</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-center w-24">Qty Produced</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-center w-28">Operator Sign</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-center w-20">QC Pass</th>
                </tr>
              </thead>
              <tbody>
                {workOrdersList.map((wo: any, idx: number) => {
                  const stageNum = wo.stageNumber || wo.stage_number || idx + 1;
                  const stageName = wo.stageName || wo.stage_name || '—';
                  const wcName = workCenterLookup.labelOf(wo.workCenterId ?? wo.work_center_id, wo.workCenterName || wo.work_center_name) || '—';
                  const machine = wo.machineName || wo.machine_name || '—';
                  const mins = wo.plannedTimeMinutes || wo.planned_time_minutes || 0;

                  return (
                    <tr key={wo.id || idx} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="border border-slate-300 px-2 py-2 text-center font-bold text-slate-900">
                        {stageNum}
                      </td>
                      <td className="border border-slate-300 px-2 py-2 font-semibold text-slate-800">
                        {stageName}
                        {wo.woNumber || wo.wo_number ? (
                          <span className="block font-mono text-[10px] text-slate-500 font-normal">
                            WO: {wo.woNumber || wo.wo_number}
                          </span>
                        ) : null}
                      </td>
                      <td className="border border-slate-300 px-2 py-2 text-slate-700">
                        <div>{wcName}</div>
                        <div className="text-[10px] text-slate-500">{machine}</div>
                      </td>
                      <td className="border border-slate-300 px-2 py-2 text-right font-mono">
                        {mins}
                      </td>
                      <td className="border border-slate-300 px-2 py-2 text-center font-mono">
                        {wo.completedQty || wo.completed_qty ? (
                          <span className="font-bold">{wo.completedQty || wo.completed_qty} / {poData.plannedQty}</span>
                        ) : (
                          <span className="text-slate-300">____</span>
                        )}
                      </td>
                      <td className="border border-slate-300 px-2 py-2 text-center text-slate-300 font-mono text-[10px]">
                        ________________
                      </td>
                      <td className="border border-slate-300 px-2 py-2 text-center">
                        <div className="w-4 h-4 border border-slate-400 rounded mx-auto" />
                      </td>
                    </tr>
                  );
                })}

                {workOrdersList.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="border border-slate-300 px-4 py-4 text-center text-slate-400 italic">
                      No routing stages available. Confirm PO to auto-generate Work Orders.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── MATERIAL REQUIREMENTS CHECKLIST ── */}
          <div className="mb-6">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-2 flex items-center justify-between">
              <span>Required Raw Materials & Issue Checklist</span>
              <span className="text-[10px] text-slate-400 font-normal">Items: {materialsList.length}</span>
            </h3>

            <table className="w-full text-xs border border-slate-300 border-collapse">
              <thead>
                <tr className="bg-slate-100 print:bg-slate-200 text-slate-800 font-bold border-b border-slate-300">
                  <th className="border border-slate-300 px-2 py-1.5 text-left w-24">Item Code</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-left">Item Description</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-right w-20">Req. Qty</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-center w-12">Unit</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-center w-28">Lot / Batch # Issued</th>
                  <th className="border border-slate-300 px-2 py-1.5 text-center w-20">Issuer Sign</th>
                </tr>
              </thead>
              <tbody>
                {materialsList.map((m: any, idx: number) => {
                  const itemCode = m.itemCode || m.item_code || '—';
                  const itemName = m.itemName || m.item_name || '—';
                  const unit = m.unit || m.item_unit || '';
                  const reqQty = Number(m.requiredQty ?? m.required_qty ?? ((m.qtyPerUnit || 1) * poData.plannedQty));

                  return (
                    <tr key={m.id || idx} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="border border-slate-300 px-2 py-1.5 font-mono text-slate-700">
                        {itemCode}
                      </td>
                      <td className="border border-slate-300 px-2 py-1.5 font-medium text-slate-800">
                        {itemName}
                      </td>
                      <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-bold text-slate-900">
                        {reqQty}
                      </td>
                      <td className="border border-slate-300 px-2 py-1.5 text-center text-slate-600">
                        {unit}
                      </td>
                      <td className="border border-slate-300 px-2 py-1.5 text-center text-slate-300 font-mono text-[10px]">
                        ________________
                      </td>
                      <td className="border border-slate-300 px-2 py-1.5 text-center text-slate-300 font-mono text-[10px]">
                        ______
                      </td>
                    </tr>
                  );
                })}

                {materialsList.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="border border-slate-300 px-4 py-4 text-center text-slate-400 italic">
                      No raw materials linked to this order.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── SPECIAL INSTRUCTIONS / NOTES ── */}
          {poData.notes && (
            <div className="mb-6">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-1">
                Special Manufacturing Notes
              </h3>
              <div className="border border-slate-300 rounded-lg p-3 bg-slate-50/50 print:bg-white text-xs text-slate-700 min-h-[40px]">
                {poData.notes}
              </div>
            </div>
          )}

          {/* ── SIGN-OFF FOOTER ── */}
          <div className="border-t-2 border-slate-900 pt-4 mt-6">
            <div className="grid grid-cols-3 gap-6 text-xs">
              <div>
                <p className="font-bold text-slate-800 uppercase tracking-wide mb-6">
                  1. Production Planner
                </p>
                <div className="border-b border-slate-400 h-6" />
                <p className="text-[10px] text-slate-500 mt-1">Signature & Date</p>
              </div>
              <div>
                <p className="font-bold text-slate-800 uppercase tracking-wide mb-6">
                  2. Shop Floor Supervisor
                </p>
                <div className="border-b border-slate-400 h-6" />
                <p className="text-[10px] text-slate-500 mt-1">Signature & Date</p>
              </div>
              <div>
                <p className="font-bold text-slate-800 uppercase tracking-wide mb-6">
                  3. Quality Assurance Inspector
                </p>
                <div className="border-b border-slate-400 h-6" />
                <p className="text-[10px] text-slate-500 mt-1">Signature & Date</p>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-200 text-center">
              <p className="text-[10px] text-slate-400 font-mono">
                Printed on {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} IST{companyName ? ` · ${companyName}` : ''}
              </p>
            </div>
          </div>

        </div>

        {/* ── MODAL ACTIONS (HIDDEN ON PRINT) ── */}
        <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3 print:hidden">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            className="px-5 py-2 rounded-xl bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 transition-colors shadow-sm cursor-pointer flex items-center gap-2"
          >
            <i className="ri-printer-line text-sm" />
            Print Traveler Sheet
          </button>
        </div>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #po-print-area, #po-print-area * {
            visibility: visible !important;
          }
          #po-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}

